import React, { useEffect, useState, useCallback } from 'react';
import { brainAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Brain, Loader2, Sparkles, Target, Shield, Zap, Clock,
  MessageCircle, ChevronDown, ChevronUp, ArrowRight,
  CheckCircle, AlertTriangle, TrendingUp, Lightbulb
} from 'lucide-react';
import { toast } from 'sonner';

const APPROACH_ICONS = {
  consultative: MessageCircle,
  challenger: Zap,
  solution: Lightbulb,
  relationship: Target,
};

const ENGAGEMENT_COLORS = {
  high: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200/60' },
  medium: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200/60' },
  low: { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200/60' },
};

const PRIORITY_STYLES = {
  high: 'bg-rose-50 text-rose-700 border-rose-200/60',
  medium: 'bg-amber-50 text-amber-700 border-amber-200/60',
  low: 'bg-muted text-muted-foreground border-border',
};

export default function ClosingStrategyPanel({ leadId, leadStatus }) {
  const [strategy, setStrategy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    objections: true, competitive: false, steps: true
  });

  const normalizedStatus = (leadStatus || '').toLowerCase();
  const isRelevantStage = ['negotiation', 'proposal/quote sent', 'qualified'].includes(normalizedStatus);

  const fetchStrategy = useCallback(async () => {
    setLoading(true);
    try {
      const res = await brainAPI.getClosingStrategy(leadId);
      if (res.data?.exists !== false) setStrategy(res.data);
    } catch { /* no strategy yet */ }
    finally { setLoading(false); }
  }, [leadId]);

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    try {
      const res = await brainAPI.generateClosingStrategy(leadId);
      setStrategy(res.data);
      toast.success('Closing strategy generated');
    } catch {
      toast.error('Failed to generate strategy');
    }
    finally { setGenerating(false); }
  }, [leadId]);

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => { fetchStrategy(); }, [fetchStrategy]);

  // Don't show at all for early-stage leads
  if (!isRelevantStage && !strategy?.exists) return null;

  if (loading) return (
    <Card className="border-rose-500/20" data-testid="closing-strategy-loading">
      <CardContent className="flex items-center justify-center py-6">
        <Loader2 className="w-4 h-4 animate-spin text-rose-500" />
      </CardContent>
    </Card>
  );

  const s = strategy?.strategy;

  return (
    <Card className="border-rose-500/20" data-testid="closing-strategy-panel">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-1">
          <CardTitle className="text-sm font-semibold flex items-center gap-1.5 min-w-0">
            <Target className="h-4 w-4 text-rose-500 shrink-0" />
            <span className="truncate">Closing Assistant</span>
            {s && (
              <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-0 text-[10px] shrink-0">
                Ready
              </Badge>
            )}
          </CardTitle>
          <Button
            variant={s ? 'outline' : 'default'}
            size="icon"
            onClick={handleGenerate}
            disabled={generating}
            data-testid="generate-strategy-btn"
            title={s ? 'Refresh Strategy' : 'Generate Strategy'}
            className={`h-7 w-7 shrink-0 ${!s ? 'bg-rose-600 hover:bg-rose-700 text-white' : 'border-rose-500/30 text-rose-500'}`}
          >
            {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="pt-1 pb-4">
        {generating ? (
          <div className="flex items-center justify-center py-6 gap-3" data-testid="strategy-generating">
            <div className="relative">
              <div className="w-8 h-8 border-2 border-rose-500/20 border-t-rose-500 rounded-full animate-spin" />
              <Brain className="w-3.5 h-3.5 text-rose-500 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
            </div>
            <div>
              <p className="text-sm font-medium">Crafting closing strategy...</p>
              <p className="text-[11px] text-muted-foreground">Analyzing engagement & lead profile</p>
            </div>
          </div>
        ) : s ? (
          <div className="space-y-4 mt-2">
            {/* Executive Summary */}
            <div className="p-4 rounded-xl bg-gradient-to-br from-slate-50 to-white dark:from-slate-900/30 dark:to-card border border-slate-100 dark:border-border" data-testid="executive-summary">
              <p className="text-sm leading-relaxed">{s.executive_summary}</p>
            </div>

            {/* Approach + Engagement Score */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Closing Approach */}
              {s.closing_approach && (
                <div className="p-3.5 rounded-xl border border-sky-200/60 bg-sky-50" data-testid="closing-approach">
                  <div className="flex items-center gap-2 mb-2">
                    {React.createElement(APPROACH_ICONS[s.closing_approach.style] || Target, { className: 'w-4 h-4 text-sky-600' })}
                    <span className="text-sm font-semibold text-sky-800 capitalize">{s.closing_approach.style} Approach</span>
                  </div>
                  <p className="text-xs text-sky-700/80 mb-1.5">{s.closing_approach.rationale}</p>
                  <div className="flex items-center gap-1.5 text-[11px] text-sky-600">
                    <Clock className="w-3 h-3" /> {s.closing_approach.timeline}
                  </div>
                </div>
              )}

              {/* Engagement Score */}
              {s.engagement_score && (
                <div className={`p-3.5 rounded-xl border ${(ENGAGEMENT_COLORS[s.engagement_score.level] || ENGAGEMENT_COLORS.medium).border} ${(ENGAGEMENT_COLORS[s.engagement_score.level] || ENGAGEMENT_COLORS.medium).bg}`} data-testid="engagement-score">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className={`w-4 h-4 ${(ENGAGEMENT_COLORS[s.engagement_score.level] || ENGAGEMENT_COLORS.medium).text}`} />
                    <span className={`text-sm font-semibold capitalize ${(ENGAGEMENT_COLORS[s.engagement_score.level] || ENGAGEMENT_COLORS.medium).text}`}>
                      {s.engagement_score.level} Engagement
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">{s.engagement_score.reasoning}</p>
                </div>
              )}
            </div>

            {/* Key Talking Points */}
            {s.key_talking_points?.length > 0 && (
              <div data-testid="talking-points">
                <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-2 flex items-center gap-1.5">
                  <MessageCircle className="w-3.5 h-3.5" /> Key Talking Points
                </h4>
                <div className="space-y-2">
                  {s.key_talking_points.map((tp, i) => (
                    <div key={i} className="flex gap-2.5 p-2.5 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors" data-testid={`talking-point-${i}`}>
                      <span className="text-[10px] font-bold text-[hsl(204,94%,40%)] mt-0.5 shrink-0 w-5 h-5 rounded-full bg-[hsl(204,94%,40%)]/10 flex items-center justify-center">{i + 1}</span>
                      <div>
                        <p className="text-sm font-medium">{tp.point}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{tp.context}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Objection Playbook (Collapsible) */}
            {s.objection_playbook?.length > 0 && (
              <div data-testid="objection-playbook">
                <button onClick={() => toggleSection('objections')} className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-widest mb-2 hover:text-foreground transition-colors w-full">
                  {expandedSections.objections ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  <Shield className="w-3.5 h-3.5" /> Objection Playbook ({s.objection_playbook.length})
                </button>
                {expandedSections.objections && (
                  <div className="space-y-2 animate-in fade-in slide-in-from-top-1 duration-200">
                    {s.objection_playbook.map((obj, i) => (
                      <div key={i} className="p-3 rounded-xl border border-amber-200/50 bg-amber-50/50" data-testid={`objection-${i}`}>
                        <div className="flex items-start gap-2 mb-2">
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />
                          <p className="text-sm font-medium text-amber-800">"{obj.objection}"</p>
                        </div>
                        <div className="ml-5.5 pl-5">
                          <div className="flex items-start gap-2 mb-1">
                            <ArrowRight className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" />
                            <p className="text-xs text-foreground">{obj.response}</p>
                          </div>
                          {obj.evidence && (
                            <p className="text-[11px] text-muted-foreground mt-1 ml-5 italic">{obj.evidence}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Competitive Positioning (Collapsible) */}
            {s.competitive_positioning && (
              <div data-testid="competitive-positioning">
                <button onClick={() => toggleSection('competitive')} className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-widest mb-2 hover:text-foreground transition-colors w-full">
                  {expandedSections.competitive ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  <Zap className="w-3.5 h-3.5" /> Competitive Positioning
                </button>
                {expandedSections.competitive && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 animate-in fade-in slide-in-from-top-1 duration-200">
                    {s.competitive_positioning.strengths?.length > 0 && (
                      <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200/50">
                        <h5 className="text-[10px] font-medium text-emerald-700 uppercase tracking-widest mb-1.5">Strengths</h5>
                        {s.competitive_positioning.strengths.map((st, i) => (
                          <p key={i} className="text-xs text-emerald-700/80 mb-1 flex gap-1.5">
                            <CheckCircle className="w-3 h-3 shrink-0 mt-0.5" /> {st}
                          </p>
                        ))}
                      </div>
                    )}
                    {s.competitive_positioning.differentiators?.length > 0 && (
                      <div className="p-3 rounded-lg bg-sky-50 border border-sky-200/50">
                        <h5 className="text-[10px] font-medium text-sky-700 uppercase tracking-widest mb-1.5">Differentiators</h5>
                        {s.competitive_positioning.differentiators.map((d, i) => (
                          <p key={i} className="text-xs text-sky-700/80 mb-1 flex gap-1.5">
                            <Sparkles className="w-3 h-3 shrink-0 mt-0.5" /> {d}
                          </p>
                        ))}
                      </div>
                    )}
                    {s.competitive_positioning.risk_mitigations?.length > 0 && (
                      <div className="p-3 rounded-lg bg-amber-50 border border-amber-200/50">
                        <h5 className="text-[10px] font-medium text-amber-700 uppercase tracking-widest mb-1.5">Risk Mitigations</h5>
                        {s.competitive_positioning.risk_mitigations.map((r, i) => (
                          <p key={i} className="text-xs text-amber-700/80 mb-1 flex gap-1.5">
                            <Shield className="w-3 h-3 shrink-0 mt-0.5" /> {r}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Next Steps */}
            {s.next_steps?.length > 0 && (
              <div data-testid="next-steps">
                <button onClick={() => toggleSection('steps')} className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-widest mb-2 hover:text-foreground transition-colors w-full">
                  {expandedSections.steps ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  <ArrowRight className="w-3.5 h-3.5" /> Next Steps ({s.next_steps.length})
                </button>
                {expandedSections.steps && (
                  <div className="space-y-2 animate-in fade-in slide-in-from-top-1 duration-200">
                    {s.next_steps.map((step, i) => (
                      <div key={i} className={`flex items-center justify-between p-2.5 rounded-lg border ${PRIORITY_STYLES[step.priority] || PRIORITY_STYLES.medium}`} data-testid={`next-step-${i}`}>
                        <div className="flex items-center gap-2.5">
                          <span className="w-5 h-5 rounded-full bg-muted/60 flex items-center justify-center text-[10px] font-bold shrink-0">{i + 1}</span>
                          <div>
                            <p className="text-sm font-medium">{step.action}</p>
                            <p className="text-[11px] opacity-70">{step.timing}</p>
                          </div>
                        </div>
                        <Badge variant="secondary" className="border-0 text-[10px] bg-muted/50">{step.priority}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Timestamp */}
            {strategy?.created_at && (
              <p className="text-[10px] text-muted-foreground text-right pt-2">
                Generated {new Date(strategy.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </p>
            )}
          </div>
        ) : (
          /* Empty state */
          <div className="text-center py-4" data-testid="strategy-empty">
            <p className="text-xs text-muted-foreground mb-0.5">No strategy yet.</p>
            <p className="text-xs text-muted-foreground">
              Generate an AI-powered closing playbook for this deal.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
